import {FC, useCallback, useEffect, useState} from "react"

import {useSession} from "@frontend/session"
import {
  getHostPartPrice,
  navigateToPart,
  toPartPriceRequestItem,
} from "@frontend/utils/appHostFunctions"
import {trackEvent} from "@frontend/utils/trackEvent"
import {useAddToCartMutation} from "@frontend/utils/trpc"
import {WIDGET_HEIGHT_MEDIA_QUERIES} from "@frontend/widget/styles"
import {ConfirmedCompatiblePart} from "@ri2/db/client"
import {css, cx} from "@styled-system/css"
import {vstack} from "@styled-system/patterns"

const GRADIENT_BORDER_CLASS = "ri2-gradient-border-card"
const GRADIENT_BORDER_CSS = `
  @keyframes ri2GradientBorderShiftCard {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  .${GRADIENT_BORDER_CLASS} {
    position: relative !important;
    border: 0 !important;
    background-color: #FFFFFF !important;
    background-clip: padding-box !important;
    box-shadow: 0 4px 24px rgba(0, 225, 255, 0.40) !important;
  }
  .${GRADIENT_BORDER_CLASS}::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 2px;
    background: linear-gradient(
      90deg,
      #70EEFF 0%,
      #00A9D7 25%,
      #91CCF0 50%,
      #00E1FF 75%,
      #015DA0 100%
    );
    background-size: 300% 300%;
    animation: ri2GradientBorderShiftCard 4s linear infinite;
    -webkit-mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
            mask-composite: exclude;
    pointer-events: none;
  }
`

interface Props {
  part: ConfirmedCompatiblePart
  caseId: string
  modelNumber: string | null
  compact?: boolean
  animatedGradientBorder?: boolean
}

/**
 * Card shown during fact-finding when a part has been confirmed compatible
 * via the validatePartCompatibility tool. Offers Add to Cart / View Part
 * so ecommerce customers can purchase immediately without completing diagnosis.
 *
 * Renders two layouts sharing the same DOM:
 *  - Hero (default): image on top, 4:3, content below.
 *  - Row: image left (88x88), content right. Keeps the CTA above the fold on
 *    short viewports (<= 750px), inside the widget (`compact` prop), and on
 *    narrow containers. Triggered via JS prop + CSS media query overrides.
 */
export const CompatiblePartCard: FC<Props> = ({
  part,
  caseId,
  modelNumber,
  compact = false,
  animatedGradientBorder = false,
}) => {
  const {userRcId} = useSession()
  const addToCartMutation = useAddToCartMutation()
  const [hostPrice, setHostPrice] = useState<number | undefined>()
  const [priceLoading, setPriceLoading] = useState<boolean>(true)

  useEffect(() => {
    let isMounted = true
    setHostPrice(undefined)
    setPriceLoading(true)

    // Stop showing the skeleton after 2s regardless, so a missing price
    // doesn't leave a permanent pulsing bar in the UI.
    const timeoutId = window.setTimeout(() => {
      if (isMounted) setPriceLoading(false)
    }, 2000)

    void getHostPartPrice(
      toPartPriceRequestItem({
        oem_number: part.manufacturerPartNumber,
        manufacturer_name: part.manufacturerName ?? "",
      }),
    ).then((value) => {
      if (!isMounted) return
      setHostPrice(typeof value === "number" ? value : undefined)
      setPriceLoading(false)
    })

    return (): void => {
      isMounted = false
      window.clearTimeout(timeoutId)
    }
  }, [part, modelNumber])

  const onAddToCart = useCallback(async () => {
    trackEvent(compact ? "widget_add_to_cart_early" : "chat_add_to_cart_early")

    const price = await getHostPartPrice(
      toPartPriceRequestItem({
        oem_number: part.manufacturerPartNumber,
        manufacturer_name: part.manufacturerName ?? "",
      }),
    )

    window.ri2?.addItemsToCart(
      [
        {
          id: part.id,
          oem_number: part.manufacturerPartNumber,
          price: typeof price === "number" ? price : undefined,
          name: part.name ?? undefined,
          model_number: modelNumber ?? "",
        },
      ],
      caseId,
    )

    addToCartMutation.mutate({
      userRcId,
      caseId,
      partId: part.id,
    })
  }, [part, caseId, modelNumber, addToCartMutation, userRcId, compact])

  const onViewPart = useCallback(() => {
    trackEvent(compact ? "widget_view_part_early" : "chat_view_part")
    navigateToPart(part)
  }, [part, compact])

  const hasCartIntegration = !!window.ri2?.addItemsToCart
  const canNavigate = !!window.ri2?.getPartDetailUrl || !!part.purchaseUrl
  const isUnavailable = !!part.isNoLongerAvailable
  const canAddToCart = hasCartIntegration && !isUnavailable
  const formattedPrice =
    typeof hostPrice === "number" ? `$${hostPrice.toFixed(2)}` : null

  let displayName = part.name ?? part.manufacturerPartNumber
  if (part.name && part.manufacturerPartNumber) {
    // Strip the part number from the name if it exists to avoid redundancy in the UI.
    // E.g. "Drain Pump - W10348269" -> "Drain Pump"
    const escapedPartNum = part.manufacturerPartNumber.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&",
    )
    const partNumRegex = new RegExp(
      `\\s*[-#]*\\s*${escapedPartNum}\\s*[-#]*\\s*`,
      "i",
    )
    const cleanedName = part.name.replace(partNumRegex, " ").trim()
    if (cleanedName) {
      displayName = cleanedName
    }
  }

  // JS-side layout switch. When `compact` (widget context) we force the row
  // layout. When not compact, CSS media queries below flip to row on short
  // viewports so the CTA stays visible.
  const forceRow = compact

  return (
    <div
      className={cx(
        css({
          border: "1px solid token(colors.border.secondary.200)",
          borderRadius: 12,
          overflow: "hidden",
          backgroundColor: "white",
          width: "100%",
          display: "flex",
          flexDirection: forceRow ? "row" : "column",
          alignItems: "stretch",
          maxWidth: forceRow ? 360 : 320,
          // Flip to row layout on short viewports even if not forced.
          [WIDGET_HEIGHT_MEDIA_QUERIES.compact]: {
            flexDirection: "row",
            maxWidth: 360,
          },
          // And on narrow screens.
          "@media (max-width: 480px)": {
            flexDirection: "row",
            maxWidth: 360,
          },
        }),
        animatedGradientBorder && GRADIENT_BORDER_CLASS,
      )}
    >
      {animatedGradientBorder && <style>{GRADIENT_BORDER_CSS}</style>}
      {/* Part image */}
      {part.image && (
        <div
          onClick={canNavigate ? onViewPart : undefined}
          className={css({
            flexShrink: 0,
            backgroundColor: "#F9FAFB",
            overflow: "hidden",
            cursor: canNavigate ? "pointer" : undefined,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ...(forceRow
              ? {
                  width: "96px",
                  height: "96px",
                  aspectRatio: "1 / 1",
                  margin: "12px 0 12px 12px",
                  borderRadius: "8px",
                  alignSelf: "center",
                }
              : {
                  width: "100%",
                  aspectRatio: "4 / 3",
                  maxHeight: "220px",
                }),
            [WIDGET_HEIGHT_MEDIA_QUERIES.compact]: {
              width: "96px",
              height: "96px",
              maxHeight: "96px",
              aspectRatio: "1 / 1",
              margin: "12px 0 12px 12px",
              borderRadius: "8px",
              alignSelf: "center",
            },
            "@media (max-width: 480px)": {
              width: "80px",
              height: "80px",
              maxHeight: "80px",
              aspectRatio: "1 / 1",
              margin: "12px 0 12px 12px",
              borderRadius: "8px",
              alignSelf: "center",
            },
          })}
        >
          <img
            src={part.image.url}
            alt={part.name ?? "Part"}
            className={css({
              width: "100%",
              height: "100%",
              objectFit: "cover",
            })}
          />
        </div>
      )}

      {/* Part details */}
      <div
        className={vstack({
          padding: 12,
          gap: 4,
          alignItems: "stretch",
          flex: 1,
          minWidth: 0,
        })}
      >
        {/* Part name */}
        <p
          onClick={canNavigate ? onViewPart : undefined}
          className={css({
            color: "text.primary",
            textStyle: "captionMedium",
            fontWeight: 600,
            lineClamp: 2,
            margin: 0,
            cursor: canNavigate ? "pointer" : undefined,
            _hover: canNavigate ? {textDecoration: "underline"} : undefined,
          })}
        >
          {displayName}
        </p>

        {/* Part number */}
        <p
          className={css({
            color: "text.tertiary",
            fontSize: 11,
            margin: 0,
          })}
        >
          {"Part #"}
          {part.manufacturerPartNumber}
        </p>

        {/* Unavailable warning — shown ABOVE the CTA to set expectations before
            the user tries to buy. */}
        {isUnavailable && (
          <p
            className={css({
              color: "#DC2626",
              fontSize: 11,
              fontWeight: 500,
              margin: 0,
            })}
          >
            {"Currently unavailable"}
          </p>
        )}

        {/* Price + CTA row */}
        <div
          className={css({
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            marginTop: "auto",
            minWidth: 0,
          })}
        >
          {/* Price slot — reserves height while loading to prevent CLS. */}
          <div
            className={css({
              minHeight: "20px",
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              flex: "0 1 auto",
            })}
          >
            {formattedPrice ? (
              <span
                className={css({
                  color: "text.primary",
                  fontWeight: 700,
                  fontSize: 16,
                  whiteSpace: "nowrap",
                })}
              >
                {formattedPrice}
              </span>
            ) : priceLoading ? (
              <span
                aria-hidden="true"
                className={css({
                  display: "inline-block",
                  width: "56px",
                  height: "14px",
                  borderRadius: "4px",
                  backgroundColor: "#E5E7EB",
                  opacity: 0.7,
                })}
              />
            ) : null}
          </div>

          {canAddToCart ? (
            <button
              onClick={onAddToCart}
              className={css({
                backgroundColor: "background.brand.accent",
                color: "foreground.primaryOnSolid",
                border: "none",
                borderRadius: 8,
                paddingX: 14,
                paddingY: 8,
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexGrow: 1,
                textAlign: "center",
                _hover: {
                  opacity: 0.9,
                },
              })}
            >
              {"Add to Cart"}
            </button>
          ) : canNavigate ? (
            <button
              onClick={onViewPart}
              className={css({
                backgroundColor: isUnavailable
                  ? "#F3F4F6"
                  : "background.brand.accent",
                color: isUnavailable ? "#374151" : "foreground.primaryOnSolid",
                border: isUnavailable ? "1px solid #E5E7EB" : "none",
                borderRadius: 8,
                paddingX: 14,
                paddingY: 8,
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexGrow: 1,
                textAlign: "center",
                _hover: {
                  opacity: 0.9,
                },
              })}
            >
              {"View Part"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
